const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;
/** `YYYY-MM-DD` → its parts when it is a real calendar date, else undefined. */
export function parseCalendarDate(value) {
    const match = DATE.exec(value.trim());
    if (match === null)
        return undefined;
    const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const probe = new Date(Date.UTC(y, m - 1, d));
    if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d)
        return undefined;
    return { y, m, d };
}
const pad = (n, w = 2) => String(n).padStart(w, '0');
const ymd = (epochUtcMidnight) => {
    const t = new Date(epochUtcMidnight);
    return `${pad(t.getUTCFullYear(), 4)}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};
/** True when `timeZone` is an IANA zone the runtime can render. */
export function isValidTimeZone(timeZone) {
    if (timeZone === undefined || timeZone.trim() === '')
        return false;
    try {
        new Intl.DateTimeFormat('en', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
/** The calendar date (`YYYY-MM-DD`) of an instant on the wall clock of `timeZone`. */
export function localCalendarDate(epochMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(epochMs));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year').padStart(4, '0')}-${get('month')}-${get('day')}`;
}
/** The zone's UTC offset (ms) at an instant (wall clock read as UTC, minus the instant, to the minute). */
function offsetAt(epochMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(epochMs));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 'NaN');
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute')) - Math.floor(epochMs / 60_000) * 60_000;
}
/**
 * The last instant (UTC, millisecond precision) of a calendar day on the wall clock of `timeZone`: one millisecond before
 * the first instant of the next local day (a daylight-saving gap at midnight is handled — the day then ends at the jump).
 * Undefined for an impossible date or an unknown zone.
 */
export function endOfLocalDay(date, timeZone) {
    const p = parseCalendarDate(date);
    if (p === undefined || !isValidTimeZone(timeZone))
        return undefined;
    const day = ymd(Date.UTC(p.y, p.m - 1, p.d));
    const nextUtcMidnight = Date.UTC(p.y, p.m - 1, p.d + 1);
    const next = ymd(nextUtcMidnight);
    const offsets = new Set([offsetAt(nextUtcMidnight - DAY_MS, timeZone), offsetAt(nextUtcMidnight, timeZone), offsetAt(nextUtcMidnight + DAY_MS, timeZone)]);
    const candidates = [...offsets].map((o) => nextUtcMidnight - o).sort((a, b) => a - b);
    for (const t of candidates) {
        if (localCalendarDate(t, timeZone) === next && localCalendarDate(t - 1, timeZone) === day)
            return new Date(t - 1).toISOString();
    }
    return undefined;
}
/** `issuedOn` plus a validity period, as a calendar date (a month-end clamps to the target month's last day). */
export function addValidityPeriod(issuedOn, period) {
    const p = parseCalendarDate(issuedOn);
    if (p === undefined || !Number.isInteger(period.amount) || period.amount < 1)
        return undefined;
    if (period.unit === 'day')
        return ymd(Date.UTC(p.y, p.m - 1, p.d + period.amount));
    if (period.unit !== 'month')
        return undefined;
    const total = p.m - 1 + period.amount;
    const y = p.y + Math.floor(total / 12);
    const m = total % 12;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return ymd(Date.UTC(y, m, Math.min(p.d, last)));
}
/**
 * The end of a document's validity computed from its issue date (owner answer, PR 291 question 4): the end of the day
 * `issuedOn + period` on the market's wall clock, as a UTC instant. Undefined for an impossible date, a bad period or an
 * unknown zone.
 */
export function validityEndFromIssue(issuedOn, period, timeZone) {
    const until = addValidityPeriod(issuedOn, period);
    return until === undefined ? undefined : endOfLocalDay(until, timeZone);
}
//# sourceMappingURL=validity.js.map